// Only importing what we need
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
// AutoCarousel component no longer needed as we're using CSS animation directly

// Redux imports not needed
// import { useSelector } from 'react-redux';
// import { RootState } from '@/store';
import {
  Book,
  ChevronRight,
  Search,
  Star,
  ArrowRight,
  CheckCircle,
  Building,
  PenTool,
  BarChart,
  Mail,
  Quote,
  GraduationCap,
  Award,
  ThumbsUp,
  MessageCircle
} from "lucide-react";
// import { getCookie } from '@/utils/cookies'; // Not used

const Index = () => {
  // const { user } = useSelector((state: RootState) => state.auth); // Not used
  const { toast } = useToast();
  // No longer need carousel pause state

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Success!",
      description: "Thank you for subscribing to our newsletter.",
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjxwYXRoIGQ9Ik0xNiAxNmMyLjIgMCA0IDEuOCA0IDRzLTEuOCA0LTQgNC00LTEuOC00LTQgMS44LTQgNC00em0xNiAwYzIuMiAwIDQgMS44IDQgNHMtMS44IDQtNCA0LTQtMS44LTQtNCAxLjgtNCA0LTR6TTM2IDE2YzIuMiAwIDQgMS44IDQgNHMtMS44IDQtNCA0LTQtMS44LTQtNCAxLjgtNCA0LTR6TTYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNGMwIDIuMi0xLjggNC00IDRzLTQtMS44LTQtNHptMTYgMGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0YzAgMi4yLTEuOCA0LTQgNC00LTEuOC00LTR6TTYgMTZjMi4yIDAgNCAxLjggNCA0cy0xLjggNC00IDQtNC0xLjgtNC00IDEuOC00IDQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10"></div>

        <div className="container relative z-10 mx-auto px-4 pt-32 pb-24 md:pt-40 md:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white hover:bg-white/30 border-none px-4 py-1.5 text-sm">
              The Future of Learning is Here
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white animate-fadeIn">
              Unlock Your Potential with Expert-Led Courses
            </h1>

            <p className="mt-6 text-lg text-white/80 animate-fadeIn" style={{ animationDelay: '200ms' }}>
              Join thousands of learners worldwide and master new skills with our comprehensive online courses.
            </p>

            {/* Search Bar */}
            <div className="mt-10 max-w-2xl mx-auto animate-fadeIn" style={{ animationDelay: '300ms' }}>
              <div className="flex flex-col sm:flex-row">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    placeholder="Search for courses, skills, or topics..."
                    className="pl-10 pr-4 py-6 rounded-l-lg sm:rounded-r-none border-0 text-base w-full focus-visible:ring-blue-500"
                  />
                </div>
                <Button
                  className="mt-2 sm:mt-0 rounded-lg sm:rounded-l-none sm:rounded-r-lg bg-amber-500 hover:bg-amber-600 text-white px-6 py-6 text-base font-medium"
                >
                  Search
                </Button>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn" style={{ animationDelay: '400ms' }}>
              <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                Start Learning <ChevronRight size={16} />
              </Button>
              <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                Browse Courses
              </Button>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 text-white animate-fadeIn" style={{ animationDelay: '500ms' }}>
              {[
                { value: "10K+", label: "Active Learners" },
                { value: "500+", label: "Expert Instructors" },
                { value: "1000+", label: "Courses Available" },
                { value: "95%", label: "Success Rate" },
              ].map((stat, index) => (
                <div key={index} className="p-4 rounded-lg bg-white/10 backdrop-blur-sm">
                  <div className="text-2xl md:text-3xl font-bold">{stat.value}</div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Platform Section */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">One Platform, All Solutions</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">Why Choose Our Platform
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">The ultimate hub that centralizes all your training needs and connects you with top providers in one seamless experience</p>
          </div>

          {/* Centralization Illustration */}
          <div className="max-w-2xl mx-auto mb-16 relative">
            <div className="bg-white rounded-xl shadow-md p-6 border border-muted/20 relative z-10">
              <div className="flex justify-center mb-6">
                <div className="relative w-32 h-32 bg-primary/10 rounded-full flex items-center justify-center">
                  <Building className="w-16 h-16 text-primary/70" />
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center border-2 border-white">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">One Central Platform</h3>
              <p className="text-center text-muted-foreground mb-6">We bring together the best training providers and courses in one place</p>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Providers", icon: Building, color: "bg-blue-50 text-blue-600" },
                  { label: "Courses", icon: Book, color: "bg-green-50 text-green-600" },
                  { label: "Progress", icon: BarChart, color: "bg-amber-50 text-amber-600" }
                ].map((item) => (
                  <div key={item.label} className="flex flex-col items-center">
                    <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mb-2`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Connecting lines */}
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] -z-10">
                <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-primary/20"></div>
                <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-primary/20"></div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-12 h-12 bg-primary/10 rounded-full animate-float"></div>
            <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-amber-100 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Building,
                title: "All Providers in One Place",
                description: "Access hundreds of training providers through a single, unified platform",
                color: "bg-blue-50 text-blue-600"
              },
              {
                icon: Book,
                title: "Centralized Course Catalog",
                description: "Browse and compare thousands of courses from different providers in one location",
                color: "bg-green-50 text-green-600"
              },
              {
                icon: PenTool,
                title: "Unified Enrollment System",
                description: "Register for any training with a single account - no need for multiple logins",
                color: "bg-purple-50 text-purple-600"
              },
              {
                icon: BarChart,
                title: "Centralized Progress Tracking",
                description: "Monitor all your courses and certifications in a single dashboard",
                color: "bg-amber-50 text-amber-600"
              }
            ].map((feature, index) => (
              <div
                key={feature.title}
                className="p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-500 border border-muted/30 group animate-fadeIn relative overflow-hidden"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Hover background effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white to-primary/5 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                {/* Decorative elements */}
                <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                <div className="absolute top-0 left-0 w-full h-1 bg-primary/0 group-hover:bg-primary/20 transition-all duration-500"></div>

                <div className="relative z-10">
                  <div className={`w-20 h-20 rounded-full ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500 border border-muted/20 group-hover:border-primary/30`}>
                    <feature.icon className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">{feature.description}</p>

                  {/* Stats badge */}
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-muted/20 text-xs font-medium text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-all duration-300">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {index === 0 ? "200+ verified providers" :
                     index === 1 ? "5,000+ available courses" :
                     index === 2 ? "One-click enrollment" :
                     "Real-time progress tracking"}
                  </div>

                  {/* Learn more link that appears on hover */}
                  <div className="mt-4 h-0 overflow-hidden opacity-0 group-hover:h-auto group-hover:opacity-100 transition-all duration-500">
                    <Link to={index === 0 ? "/providers" : "/courses"} className="inline-flex items-center text-primary text-sm font-medium hover:underline">
                      Learn more <ChevronRight className="ml-1 h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Partners Section */}
      <section className="py-24 bg-gradient-to-b from-blue-50/30 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">Industry Leaders</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">Our Partners
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">We collaborate with leading technology companies and educational institutions to provide the best learning experience</p>
          </div>

          <div className="mb-16">
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
                <Badge className="bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 border-none px-3 py-1 font-medium">
                  E-Learning Partners
                </Badge>
                <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 border-none px-3 py-1 font-medium">
                  Technology Partners
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground italic">
                Scroll horizontally to see more partners
              </div>
            </div>

            <div className="relative bg-white/50 rounded-xl p-4 border border-muted/10">
              {/* Left shadow gradient */}
              <div className="absolute left-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-white via-white/90 to-transparent pointer-events-none"></div>

              {/* Right shadow gradient */}
              <div className="absolute right-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-l from-white via-white/90 to-transparent pointer-events-none"></div>

              <div
                className="flex overflow-x-auto py-4 scrollbar-hide"
                style={{ scrollBehavior: 'smooth' }}
              >
                <div className="flex flex-nowrap gap-6">
                  {[
                    /* E-Learning Partners */
                    {
                      name: "Udemy",
                      logo: "https://www.vectorlogo.zone/logos/udemy/udemy-ar21.svg",
                      category: "Online Learning Platform",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    {
                      name: "Coursera",
                      logo: "https://www.vectorlogo.zone/logos/coursera/coursera-ar21.svg",
                      category: "Online Learning Platform",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    {
                      name: "edX",
                      logo: "https://www.vectorlogo.zone/logos/edx/edx-ar21.svg",
                      category: "Online Learning Platform",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    {
                      name: "Khan Academy",
                      logo: "https://www.vectorlogo.zone/logos/khanacademy/khanacademy-ar21.svg",
                      category: "Educational Platform",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    {
                      name: "Pluralsight",
                      logo: "https://www.vectorlogo.zone/logos/pluralsight/pluralsight-ar21.svg",
                      category: "Technology Learning",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    {
                      name: "LinkedIn Learning",
                      logo: "https://www.vectorlogo.zone/logos/linkedin/linkedin-ar21.svg",
                      category: "Professional Development",
                      color: "bg-white",
                      type: "e-learning"
                    },
                    /* Technology Partners */
                    {
                      name: "Google",
                      logo: "https://www.vectorlogo.zone/logos/google/google-ar21.svg",
                      category: "Technology",
                      color: "bg-white",
                      type: "tech"
                    },
                    {
                      name: "Amazon Web Services",
                      logo: "https://www.vectorlogo.zone/logos/amazon_aws/amazon_aws-ar21.svg",
                      category: "Cloud Computing",
                      color: "bg-white",
                      type: "tech"
                    },
                    {
                      name: "Microsoft",
                      logo: "https://www.vectorlogo.zone/logos/microsoft/microsoft-ar21.svg",
                      category: "Technology",
                      color: "bg-white",
                      type: "tech"
                    },
                    {
                      name: "IBM",
                      logo: "https://www.vectorlogo.zone/logos/ibm/ibm-ar21.svg",
                      category: "Technology",
                      color: "bg-white",
                      type: "tech"
                    },
                    {
                      name: "Salesforce",
                      logo: "https://www.vectorlogo.zone/logos/salesforce/salesforce-ar21.svg",
                      category: "CRM",
                      color: "bg-white",
                      type: "tech"
                    },
                    {
                      name: "Oracle",
                      logo: "https://www.vectorlogo.zone/logos/oracle/oracle-ar21.svg",
                      category: "Database",
                      color: "bg-white",
                      type: "tech"
                    }
                  ].map((partner, index) => (
                    <TooltipProvider key={`${partner.name}-${index}`}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`${partner.color} p-4 rounded-xl shadow-sm hover:shadow-lg border ${partner.type === 'e-learning' ? 'border-amber-200/30' : 'border-blue-200/30'} flex items-center justify-center transition-all duration-300 group flex-shrink-0 w-[180px] h-[80px]`}
                          >
                            <div className="relative overflow-hidden w-full h-full flex items-center justify-center">
                              <div className={`absolute inset-0 bg-gradient-to-br ${partner.type === 'e-learning' ? 'from-amber-50/30' : 'from-blue-50/30'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl`}></div>
                              <img
                                src={partner.logo}
                                alt={partner.name}
                                className="max-h-[80%] max-w-[80%] object-contain transition-transform duration-300 group-hover:scale-110 relative z-10"
                              />
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">{partner.name}</p>
                          <p className="text-xs text-muted-foreground">{partner.category}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-white rounded-xl p-8 shadow-lg border border-muted/10 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-primary/5 rounded-full"></div>
            <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-blue-50 rounded-full"></div>

            <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <Badge className="mb-4 bg-primary/20 text-primary hover:bg-primary/30 border-none px-3 py-1 font-medium">Partnership Opportunities</Badge>
                <h3 className="text-2xl font-bold mb-4">Become Our Partner</h3>
                <p className="text-muted-foreground mb-6">
                  Join our network of industry-leading partners and help shape the future of learning.
                  We're looking for organizations that share our vision for accessible, high-quality education.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button className="flex items-center gap-2 px-6">
                    Join Now <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                  <Button variant="outline" className="flex items-center gap-2">
                    Learn More <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50/50 to-white p-6 rounded-lg shadow-sm border border-muted/10">
                <h4 className="font-bold text-lg mb-4 flex items-center">
                  <Star className="h-5 w-5 text-amber-500 mr-2 fill-amber-500" />
                  Partnership Benefits
                </h4>
                <ul className="space-y-4">
                  {[
                    "Access to our global network of learners",
                    "Co-branded educational content and courses",
                    "Joint marketing and promotional opportunities",
                    "Priority listing in our provider directory",
                    "Advanced analytics and reporting tools"
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <div className="mt-0.5 bg-primary/10 p-1.5 rounded-full group-hover:bg-primary/20 transition-colors duration-300">
                        <CheckCircle className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-white to-muted/20 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-10 w-40 h-40 bg-primary/5 rounded-full animate-float opacity-70"></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-indigo-100/30 rounded-full animate-float opacity-50" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-amber-100/20 rounded-full animate-float opacity-40" style={{ animationDelay: '0.8s' }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">Student Success Stories</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">What Our Students Say
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">Hear from our community of learners about their experiences and transformative journeys</p>
          </div>

          {/* Testimonial Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 max-w-4xl mx-auto">
            {[
              { icon: GraduationCap, value: "15,000+", label: "Success Stories", color: "blue" },
              { icon: Star, value: "4.8/5", label: "Average Rating", color: "amber" },
              { icon: Award, value: "92%", label: "Completion Rate", color: "green" },
              { icon: ThumbsUp, value: "97%", label: "Would Recommend", color: "purple" }
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`bg-white rounded-xl p-4 shadow-md border border-muted/10 text-center group hover:shadow-lg transition-all duration-300 animate-fadeIn relative overflow-hidden`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`absolute top-0 left-0 w-full h-1 ${stat.color === 'blue' ? 'bg-blue-500/70' : stat.color === 'amber' ? 'bg-amber-500/70' : stat.color === 'green' ? 'bg-green-500/70' : 'bg-purple-500/70'}`}></div>
                <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-muted/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>

                <div className="relative z-10">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${stat.color === 'blue' ? 'bg-blue-50 text-blue-500' : stat.color === 'amber' ? 'bg-amber-50 text-amber-500' : stat.color === 'green' ? 'bg-green-50 text-green-500' : 'bg-purple-50 text-purple-500'}`}>
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div className="text-xl font-bold mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Featured Testimonial */}
          <div className="mb-16 max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-muted/10 overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&auto=format&fit=crop"
                  alt="Student learning online"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-indigo-600/80 mix-blend-multiply"></div>
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="text-white text-center">
                    <Quote className="h-12 w-12 mx-auto mb-4 text-white/80" />
                    <p className="text-xl font-medium italic mb-6 leading-relaxed">
                      "LearnHub completely transformed my career path. The skills I gained helped me land my dream job within just 3 months of completing my courses."
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">Alex Rivera</h3>
                  <p className="text-primary font-medium">Data Scientist at TechCorp</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/10 p-1.5 rounded-full">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base">Career Transition</h4>
                      <p className="text-sm text-muted-foreground">Switched from marketing to data science in less than a year</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/10 p-1.5 rounded-full">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base">Salary Increase</h4>
                      <p className="text-sm text-muted-foreground">Achieved a 45% salary increase after certification</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/10 p-1.5 rounded-full">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base">Completed 5 Courses</h4>
                      <p className="text-sm text-muted-foreground">Data Science Specialization with Python and Machine Learning</p>
                    </div>
                  </div>
                </div>

                <Button variant="outline" className="flex items-center gap-2 w-fit">
                  <MessageCircle className="h-4 w-4" /> Read Full Story
                </Button>
              </div>
            </div>
          </div>

          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah Johnson",
                role: "UX Designer at CreativeStudio",
                image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
                quote: "The courses here have transformed my career. The instructors are amazing and the content is top-notch.",
                rating: 5,
                course: "UX/UI Design Masterclass",
                achievement: "Redesigned company website, increasing conversions by 32%"
              },
              {
                name: "Michael Chen",
                role: "Senior Software Engineer",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
                quote: "I've taken several courses and each one has helped me grow professionally. The platform is incredibly user-friendly.",
                rating: 5,
                course: "Advanced Web Development",
                achievement: "Built a startup MVP that secured $500K in funding"
              },
              {
                name: "Emily Roberts",
                role: "Digital Marketing Director",
                image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
                quote: "The quality of education here is unmatched. I've recommended it to all my colleagues.",
                rating: 4,
                course: "Digital Marketing Strategy",
                achievement: "Increased company social media engagement by 215%"
              }
            ].map((testimonial, index) => (
              <div
                key={testimonial.name}
                className="bg-white rounded-xl shadow-md hover:shadow-xl border border-muted/10 transition-all duration-300 animate-fadeIn relative overflow-hidden group"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Top accent bar that appears on hover */}
                <div className="absolute top-0 left-0 w-full h-1 bg-primary/0 group-hover:bg-primary/40 transition-all duration-500"></div>

                {/* Rating badge */}
                <div className="absolute top-4 right-4 bg-white shadow-md rounded-full px-2 py-1 flex items-center gap-1 z-20">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-3 w-3 ${i < testimonial.rating ? 'fill-amber-500 text-amber-500' : 'text-muted/40'}`}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-medium ml-1">{testimonial.rating}.0</span>
                </div>

                {/* Profile section */}
                <div className="p-6 pb-4 flex items-center gap-4 border-b border-muted/10">
                  <div className="relative">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 group-hover:border-primary/40 transition-colors duration-300"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-primary text-white p-1 rounded-full shadow-md">
                      <CheckCircle className="h-3 w-3" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg group-hover:text-primary transition-colors duration-300">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>

                {/* Quote section */}
                <div className="p-6 pt-5">
                  <div className="mb-4 relative">
                    <Quote className="absolute -top-1 -left-1 h-5 w-5 text-primary/20" />
                    <p className="text-muted-foreground leading-relaxed pl-4">{testimonial.quote}</p>
                  </div>

                  {/* Course and achievement info */}
                  <div className="mt-6 pt-4 border-t border-muted/10">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="mt-0.5 bg-primary/10 p-1 rounded-full">
                        <GraduationCap className="h-3 w-3 text-primary" />
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Course: </span>
                        <span className="font-medium">{testimonial.course}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 bg-primary/10 p-1 rounded-full">
                        <Award className="h-3 w-3 text-primary" />
                      </div>
                      <div className="text-xs">
                        <span className="text-muted-foreground">Achievement: </span>
                        <span className="font-medium">{testimonial.achievement}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative corner shape */}
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="px-8 shadow-md">
              Read More Success Stories <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-gradient-to-r from-primary/90 to-indigo-600 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-400/10 rounded-full blur-3xl"></div>

        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 30 + 10}px`,
                height: `${Math.random() * 30 + 10}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${Math.random() * 10 + 15}s`,
                animationDelay: `${Math.random() * 5}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="text-left">
              <Badge className="mb-6 bg-white/20 text-white hover:bg-white/30 border-none">Never Miss an Update</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Stay Updated with LearnHub</h2>
              <p className="text-white/80 mb-8 text-lg">
                Join our newsletter and be the first to know about new courses, exclusive offers, and expert tips to accelerate your learning journey.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {[
                  {
                    title: "Course Alerts",
                    description: "Get notified when new courses in your field of interest are available"
                  },
                  {
                    title: "Learning Resources",
                    description: "Access free guides, checklists, and educational materials"
                  },
                  {
                    title: "Exclusive Offers",
                    description: "Receive special discounts and early access to premium content"
                  },
                  {
                    title: "Industry Insights",
                    description: "Stay informed about trends and developments in your field"
                  }
                ].map((item, index) => (
                  <div key={item.title} className="flex items-start gap-3 group animate-fadeIn" style={{ animationDelay: `${index * 150}ms` }}>
                    <div className="mt-1 bg-white/10 p-1.5 rounded-full group-hover:bg-white/20 transition-colors duration-300 flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-1 group-hover:text-white/90 transition-colors duration-300">{item.title}</h4>
                      <p className="text-sm text-white/70 group-hover:text-white/80 transition-colors duration-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/0"></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="h-8 w-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold mb-6 text-center">Subscribe to Our Newsletter</h3>

                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Full Name</label>
                      <input
                        type="text"
                        placeholder="Your name"
                        className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-4 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Email Address</label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-4 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Interests (Optional)</label>
                      <select className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent appearance-none">
                        <option value="" className="bg-primary text-white">Select your primary interest</option>
                        <option value="web-development" className="bg-primary text-white">Web Development</option>
                        <option value="data-science" className="bg-primary text-white">Data Science & AI</option>
                        <option value="business" className="bg-primary text-white">Business & Management</option>
                        <option value="design" className="bg-primary text-white">Design & Creative</option>
                        <option value="personal" className="bg-primary text-white">Personal Development</option>
                      </select>
                    </div>

                    <div className="flex items-start gap-2 mt-4">
                      <input type="checkbox" id="privacy" className="mt-1" required />
                      <label htmlFor="privacy" className="text-sm text-white/70">
                        I agree to receive emails from LearnHub. You can unsubscribe at any time. View our <a href="#" className="underline hover:text-white">Privacy Policy</a>.
                      </label>
                    </div>

                    <Button type="submit" className="w-full bg-white text-primary hover:bg-white/90 mt-2 shadow-lg">
                      Subscribe Now
                    </Button>
                  </form>

                  <div className="mt-6 pt-6 border-t border-white/10 text-center">
                    <p className="text-sm text-white/70 flex items-center justify-center gap-2">
                      <span className="inline-block p-1 bg-green-500/20 rounded-full">
                        <CheckCircle className="h-3 w-3 text-green-400" />
                      </span>
                      We respect your privacy and will never share your information
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-indigo-500/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/30 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">LearnHub</h3>
              <p className="text-sm text-muted-foreground">
                Empowering learners worldwide with quality education.
              </p>
            </div>
            {[
              {
                title: "Company",
                links: ["About", "Careers", "Press", "Contact"]
              },
              {
                title: "Resources",
                links: ["Blog", "Newsletter", "Help Center", "Tutorials"]
              },
              {
                title: "Legal",
                links: ["Terms", "Privacy", "Cookies", "Licenses"]
              }
            ].map((section) => (
              <div key={section.title}>
                <h3 className="font-medium mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link}>
                      <a
                        href="#"
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-muted">
            <p className="text-sm text-center text-muted-foreground">
              © 2024 LearnHub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
